// frontend/src/pages/PrivacyPolicy/PrivacyPolicy.jsx

import React, { useCallback, useState, useRef } from 'react';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import { useTranslation } from 'react-i18next';
import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import UniversalNav from '../../components/UniversalNav/UniversalNav';
import './LegalPages.css'; // Use the new shared stylesheet

const PrivacyPolicy = () => {
    const { t } = useTranslation();
    const [isScrolled, setIsScrolled] = useState(false);
    const containerRef = useRef(null);

    const handleScroll = (e) => {
        if (e.target.scrollTop > 20) {
            setIsScrolled(true);
        } else {
            setIsScrolled(false);
        }
    };

    const particlesInit = useCallback(async (engine) => {
        await loadSlim(engine);
    }, []);

    const particlesConfig = {
        background: { color: { value: 'transparent' } },
        fpsLimit: 120,
        interactivity: { events: { onHover: { enable: true, mode: 'repulse' }, resize: true }, modes: { repulse: { distance: 100, duration: 0.4 } } },
        particles: {
            color: { value: '#ffffff' },
            links: { color: '#4fc3f7', distance: 150, enable: true, opacity: 0.4, width: 1 },
            move: { direction: 'none', enable: true, outModes: { default: 'bounce' }, random: false, speed: 1, straight: false },
            number: { density: { enable: true, area: 1000 }, value: 50 }, // Less dense for text-heavy pages
            opacity: { value: 0.5 },
            shape: { type: 'circle' },
            size: { value: { min: 1, max: 3 } },
        },
        detectRetina: true,
    };

    return (
        <div 
            ref={containerRef}
            onScroll={handleScroll}
            className={`legal-page-container ${isScrolled ? 'scrolled' : ''}`}
        >
            <Particles id="tsparticles-legal" init={particlesInit} options={particlesConfig} />
            <Helmet>
                <title>{t('privacyPolicy.helmet.title')}</title>
                <meta name="description" content={t('privacyPolicy.helmet.description')} />
            </Helmet>

            <div className="legal-nav-container">
                <UniversalNav />
            </div>

            <main className="legal-content-wrapper">
                <Box className="legal-glass-container">
                    <Box className="legal-header">
                        <Typography variant="h3" component="h1">
                            {t('privacyPolicy.mainTitle')}
                        </Typography>
                        <Typography variant="subtitle1" component="p">
                            {t('privacyPolicy.lastUpdated')}
                        </Typography>
                    </Box>

                    <Box className="legal-section">
                        <Typography paragraph>{t('privacyPolicy.intro.p1')}</Typography>
                        <Typography paragraph>{t('privacyPolicy.intro.p2')}</Typography>
                    </Box>

                    <Box className="legal-section">
                        <Typography variant="h5" component="h2">{t('privacyPolicy.section1.title')}</Typography>
                        <Typography paragraph>{t('privacyPolicy.section1.intro')}</Typography>
                        <Typography paragraph sx={{ mt: 2 }}>
                            <strong>{t('privacyPolicy.section1.provided.title')}</strong>
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText
                                    primary={t('privacyPolicy.section1.provided.accountInfo.label')}
                                    secondary={t('privacyPolicy.section1.provided.accountInfo.text')}
                                />
                            </ListItem>
                            {/* NEW: Billing Info */}
                            <ListItem>
                                <ListItemText
                                    primary={t('privacyPolicy.section1.provided.billingInfo.label')}
                                    secondary={t('privacyPolicy.section1.provided.billingInfo.text')}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary={t('privacyPolicy.section1.provided.analyzedContent.label')}
                                    secondary={t('privacyPolicy.section1.provided.analyzedContent.text')}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary={t('privacyPolicy.section1.provided.generatedOutputs.label')}
                                    secondary={t('privacyPolicy.section1.provided.generatedOutputs.text')}
                                />
                            </ListItem>
                        </List>

                        <Typography paragraph sx={{ mt: 2 }}>
                            <strong>{t('privacyPolicy.section1.features.title')}</strong>
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText
                                    primary={t('privacyPolicy.section1.features.usageData.label')}
                                    secondary={t('privacyPolicy.section1.features.usageData.text')}
                                />
                            </ListItem>
                        </List>

                        <Typography paragraph sx={{ mt: 2 }}>
                            <strong>{t('privacyPolicy.section1.automatic.title')}</strong>
                        </Typography>
                        <Typography paragraph>{t('privacyPolicy.section1.automatic.text')}</Typography>
                    </Box>

                    <Box className="legal-section">
                        <Typography variant="h5" component="h2">{t('privacyPolicy.section2.title')}</Typography>
                        <Typography paragraph>{t('privacyPolicy.section2.intro')}</Typography>
                        <List>
                            <ListItem>
                                <ListItemText primary={t('privacyPolicy.section2.points.accountCreation')} />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary={t('privacyPolicy.section2.points.manageAccounts.label')}
                                    secondary={t('privacyPolicy.section2.points.manageAccounts.text')}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary={t('privacyPolicy.section2.points.provideService.label')}
                                    secondary={t('privacyPolicy.section2.points.provideService.text')}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary={t('privacyPolicy.section2.points.respondToInquiries')} />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary={t('privacyPolicy.section2.points.publicVerification.label')}
                                    secondary={t('privacyPolicy.section2.points.publicVerification.text')}
                                />
                            </ListItem>
                        </List>
                    </Box>

                    {/* ... other sections also use 'legal-section' ... */}
                    <Box className="legal-section">
                        <Typography variant="h5" component="h2">{t('privacyPolicy.section3.title')}</Typography>
                        <Typography paragraph>{t('privacyPolicy.section3.text')}</Typography>
                    </Box>
                    <Box className="legal-section">
                        <Typography variant="h5" component="h2">{t('privacyPolicy.section4.title')}</Typography>
                        <Typography paragraph>{t('privacyPolicy.section4.text')}</Typography>
                    </Box>
                    <Box className="legal-section">
                        <Typography variant="h5" component="h2">{t('privacyPolicy.section5.title')}</Typography>
                        <Typography paragraph>{t('privacyPolicy.section5.intro')}</Typography>
                        <List>
                            <ListItem>
                                <ListItemText
                                    primary={t('privacyPolicy.section5.points.inputs.label')}
                                    secondary={t('privacyPolicy.section5.points.inputs.text')}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary={t('privacyPolicy.section5.points.outputs.label')}
                                    secondary={t('privacyPolicy.section5.points.outputs.text')}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary={t('privacyPolicy.section5.points.ledger.label')}
                                    secondary={t('privacyPolicy.section5.points.ledger.text')}
                                />
                            </ListItem>
                        </List>
                    </Box>
                </Box>
            </main>
        </div>
    );
};

export default PrivacyPolicy;